import { LoadingSpinner } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useTravelPageRollout } from "@src/appV2/redesign/Shift/TravelBlock/useTravelPageRollout";
import { SHIFT_BLOCKS_PATH } from "@src/appV2/redesign/ShiftDiscovery/Blocks/paths";
import { SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useShiftModalsDataContext";
import { useFetchTravelBlocks } from "@src/appV2/TravelBlocks/api/useFetchTravelBlocks";
import { type Worker } from "@src/appV2/Worker/api/types";
import { keyBy } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

interface TravelBlocksProps {
  worker: Worker;
}

export function TravelBlocks(props: TravelBlocksProps) {
  const { worker } = props;

  useLogEffect(APP_V2_APP_EVENTS.TRAVEL_BLOCKS_PAGE_VIEWED, {
    workerId: worker.userId,
  });

  const history = useHistory();

  const [isIframeReadyToListen, setIsIframeReadyToListen] = useState(false);

  const { baseNavigationPath } = useShiftModalsDataContext();

  const { travelPageUrl, pageSize } = useTravelPageRollout();

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const {
    data: travelBlocksData,
    isLoading: isTravelBlocksLoading,
    isSuccess: isTravelBlocksSuccess,
  } = useFetchTravelBlocks(
    {
      page: {
        size: pageSize,
      },
    },
    {
      meta: {
        userErrorMessage: "Something went wrong while loading shift blocks",
      },
    }
  );

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== travelPageUrl) {
        return;
      }

      switch (event.data.type) {
        case "open-travel-block": {
          const { id } = event.data as { id: string };

          if (isDefined(id)) {
            history.push(
              generatePath(`${RootPaths.APP_V2_HOME}/${SHIFT_BLOCKS_PATH}/:shiftBlockId`, {
                shiftBlockId: id,
              })
            );
          }

          break;
        }

        case "open-workplace-profile": {
          const { id } = event.data as { id: string };

          if (isDefined(id)) {
            history.push(
              generatePath(SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH, {
                workplaceId: id,
              })
            );
          }

          break;
        }

        case "ready-to-listen": {
          setIsIframeReadyToListen(true);

          break;
        }

        case "go-back": {
          history.push(baseNavigationPath);

          break;
        }

        default: {
          break;
        }
      }
    }

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [baseNavigationPath, history, travelPageUrl]);

  const transformedData = useMemo(() => {
    if (isTravelBlocksSuccess) {
      const shifts = travelBlocksData.included.filter((included) => included.type === "shift");

      const shiftsMap = keyBy(shifts, "id");

      return {
        worker: {
          id: worker.userId,
          address: {
            fullAddress: worker.address?.formatted,
            longitude: worker.geoLocation?.coordinates[0],
            latitude: worker.geoLocation?.coordinates[1],
          },
        },
        travelBlocks: travelBlocksData.data.map((shiftBlock) => {
          return {
            id: shiftBlock.id,
            qualification: shiftBlock.attributes.agentReq,
            start: shiftBlock.attributes.blockStartTime,
            end: shiftBlock.attributes.blockEndTime,
            totalPay: shiftBlock.attributes.totalPay,
            totalTime: shiftBlock.attributes.totalTime,
            workplaceId: shiftBlock.attributes.facilityId,
            notesForProfessionals: shiftBlock.attributes.notesForProfessionals,
            shifts: shiftBlock.relationships.shifts.data.map((shift) => {
              const shiftData = shiftsMap[shift.id];

              return {
                id: shift.id,
                start: shiftData.attributes.start,
                end: shiftData.attributes.end,
                pay: shiftData.attributes.pay,
                time: shiftData.attributes.time,
                isInstantPay: shiftData.attributes.isInstantPay,
              };
            }),
          };
        }),
        workplaces: travelBlocksData.included
          .filter((included) => included.type === "facility")
          .map((facility) => {
            return {
              id: facility.id,
              name: facility.attributes.name,
              distance: facility.attributes.distance,
              fullAddress: facility.attributes.fullAddress?.formatted,
              latitude: facility.attributes.geoLocation?.coordinates[1],
              longitude: facility.attributes.geoLocation?.coordinates[0],
              stateCode: facility.attributes.fullAddress.stateCode,
              city: facility.attributes.fullAddress.city,
            };
          }),
      };
    }

    return null;
  }, [isTravelBlocksSuccess, travelBlocksData, worker]);

  useEffect(() => {
    if (isIframeReadyToListen && isTravelBlocksSuccess && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        {
          type: "travel-blocks-data",
          data: transformedData,
        },
        travelPageUrl
      );
    }
  }, [isIframeReadyToListen, isTravelBlocksSuccess, transformedData, travelPageUrl]);

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {isTravelBlocksLoading && (
        <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
          <LoadingSpinner size="large" variant="primary" />
        </Stack>
      )}

      {isDefined(travelPageUrl) && (
        <iframe
          ref={iframeRef}
          src={travelPageUrl}
          title="Travel Blocks"
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      )}
    </Box>
  );
}
